// screens/Review.tsx — B16d: the holding pen decides. One queue, each item
// in its target-type card, and nothing machine-made takes effect until the
// decision on THIS screen (or the same object opened from its thread —
// same proposal, same doors).
//
//   email_draft — the full card: to/subject read, BODY EDITABLE IN PLACE,
//     rationale shown, Send / Discard. Send = the approve door with the
//     edited text as overrides (B14's contract: the human's edits are what
//     gets sent; the model's original stays on the proposal row). This card
//     is exactly why B16a refused a generic approve here: a third-party
//     email must be readable in full where it is sent from.
//   commitment (and commitment_close) — approve / decline, the existing
//     doors.
//   anything else — named, with approve/decline; an unknown kind is never
//     hidden, and a refusal from the server renders as itself.
//
// A decided item leaves the pending list with a RECEIPT line (what
// happened), not a vanish — receipts, not acknowledgements.
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  approveClientLetter,
  approveClientWork,
  approveEmailDraft,
  approveGoogleEventChange,
  approveIntakeTriage,
  approvePostDocument,
  approveProgramme,
  approveProposal,
  countPendingProposals,
  createPublishPost,
  declineProgramme,
  killPostDocument,
  listProposals,
  listPublishAccounts,
  rejectProposal,
  resplitEditedSentences,
  undoGoogleEventChange,
  type ClientLetterProposedApi,
  type ClientWorkProposedApi,
  type ConnectedAccountApi,
  type IntakeTriageProposedApi,
  type PostDocumentCitationApi,
  type PostDocumentProposedApi,
  type Proposal,
  type ProgrammeProposedApi,
} from '../lib/api';
import {
  captureExcerpt,
  captureOutcomeLines,
  captureProvenanceLine,
  captureUnreadLine,
  commitmentDueLine,
  proposalKindLabel,
  proposalSummaryLine,
} from '../lib/proposals';
import { routePath } from '../routes/route';
import './Review.css';

export interface ReviewProps {
  tenantId: string;
  // Chunk PRG-W — the programme card's "Edit" opens the programme's own
  // screen (step edit lives there); optional so every other call site that
  // mounts Review without it is untouched.
  navigate?: (path: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready' };

interface Receipt {
  id: string;
  line: string;
  /** A change made in Google keeps its Undo on its receipt (b4f991ad rule 4). */
  undo?: { changeId: string; guests: number };
}

// atlas's brief 2026-10-12 (d1cd7c74) — the assistant's proposed change to a
// Google event, read off the proposal (calendarEdit.ts propose()).
interface GoogleEventChangeFields {
  op: 'edit' | 'cancel';
  title: string;
  calendar: string;
  guests: string[];
  lines: string[];
}
function googleEventChangeFields(p: Proposal): GoogleEventChangeFields {
  const proposed = p.proposed as {
    op?: 'edit' | 'cancel';
    title?: string;
    calendar?: string;
    guests?: string[];
    change?: { title?: string; start?: string; end?: string | null; location?: string | null; notes?: string | null } | null;
  };
  const c = proposed.change ?? {};
  const lines: string[] = [];
  if (c.title !== undefined) lines.push(`Title → ${c.title}`);
  if (c.start !== undefined) lines.push(`When → ${c.start.replace('T', ' ')}${c.end ? ` – ${c.end.slice(11) || c.end}` : ''}`);
  if (c.location !== undefined) lines.push(c.location ? `Where → ${c.location}` : 'Where → (none)');
  if (c.notes !== undefined) lines.push('Notes → changed');
  return {
    op: proposed.op === 'cancel' ? 'cancel' : 'edit',
    title: proposed.title ?? 'an event',
    calendar: proposed.calendar ?? 'Google',
    guests: proposed.guests ?? [],
    lines,
  };
}
function organizerAskFields(p: Proposal): { to: string; subject: string; body: string } {
  const d = (p.proposed as { draft?: { to?: string[]; subject?: string; body?: string } }).draft ?? {};
  return { to: (d.to ?? []).join(', '), subject: d.subject ?? '', body: d.body ?? '' };
}

/** ⚑ THE KIND'S NAME NOW LIVES IN lib/proposals.ts, not here.
 *
 * G-19 put the same pending rows on the household's Today, and two screens
 * naming the same proposal differently is the drift this codebase keeps
 * paying for. The words are unchanged; only their address is. */
const kindLabel = proposalKindLabel;

function triageFields(p: Proposal): IntakeTriageProposedApi {
  return p.proposed as unknown as IntakeTriageProposedApi;
}

// The model's own confidence vocabulary (thin/workable/rich), in a person's
// words — how much the enquiry gave us to go on.
const TRIAGE_CONFIDENCE_LABEL: Record<string, string> = {
  thin: 'little to go on',
  workable: 'enough to go on',
  rich: 'plenty to go on',
};

function workFields(p: Proposal): ClientWorkProposedApi {
  return p.proposed as unknown as ClientWorkProposedApi;
}

function letterFields(p: Proposal): ClientLetterProposedApi {
  return p.proposed as unknown as ClientLetterProposedApi;
}

function postFields(p: Proposal): PostDocumentProposedApi {
  return p.proposed as unknown as PostDocumentProposedApi;
}

/** The citations that back one sentence — publish/rail.ts's own scoping
 * rule, mirrored on the client so the card shows exactly what the rail
 * would check: an absent/empty `citationIndexes` means every citation on
 * the candidate counts. */
function citationsForSentence(
  sentence: PostDocumentProposedApi['sentences'][number],
  all: PostDocumentCitationApi[]
): PostDocumentCitationApi[] {
  if (!sentence.citationIndexes || sentence.citationIndexes.length === 0) return all;
  return sentence.citationIndexes.map((i) => all[i]).filter((c): c is PostDocumentCitationApi => !!c);
}

/** The reading, rendered plainly. The summary arrives as markdown from a
 * model; this shows it as the text it is rather than pretending to a
 * renderer the cockpit does not have — headings keep their weight, and
 * nothing is silently dropped. */
function SummaryBlock({ md }: { md: string }) {
  return (
    <div class="review__triage-summary">
      {md.split('\n').map((line, i) => {
        const heading = /^#{1,3}\s+/.test(line);
        const text = line.replace(/^#{1,3}\s+/, '').replace(/\*\*/g, '');
        if (!text.trim()) return null;
        return (
          <div class={heading ? 'review__triage-h' : 'review__triage-p'} key={`${i}-${text.slice(0, 12)}`}>
            {text}
          </div>
        );
      })}
    </div>
  );
}

/** PC3 design §6's own receipt wording, verbatim, built from the approve
 * response's own `deliveries[]` — never guessed on the client. */
function publishApproveReceipt(
  channel: string,
  recordDisplayName: string,
  deliveries: { status: 'queued' | 'held'; platform: string }[]
): string {
  if (deliveries.length === 0) return `Approved for ${channel} — ${recordDisplayName}`;
  const queued = deliveries.filter((d) => d.status === 'queued');
  if (queued.length > 0) {
    return `Approved · ${queued.length} deliver${queued.length === 1 ? 'y' : 'ies'} queued for ${queued.map((d) => d.platform).join(', ')}`;
  }
  return 'Approved · held — publish.enabled is off';
}

/** The generic card's body: the proposed record itself, title first, every
 * remaining field as a plain key/value line. Nothing is approved unseen.
 *
 * ⚑ NOTHING IS STRINGIFIED ANY MORE (Day 2 walk, S3.3). This card used to
 * print `JSON.stringify(v)` for any value that was not a string, which is fine
 * for a number and is a machine dump for an array of engine instructions — the
 * screen that greeted every forwarded document opened with
 * `actions: [{"op":"createRecord",…}]`. A nested value is the ENGINE's, not the
 * reader's: it is skipped here, and a payload whose whole content was nested
 * says so in one sentence rather than printing the object. Capture proposals
 * do not reach this card at all — they have their own, below.
 *
 * ⚠ AND WHEN THERE IS NOTHING PLAIN LEFT TO SHOW, THE CARD SAYS SO. A payload
 * made entirely of nested values renders one sentence naming that fact — not
 * an empty card, and not the object. The decision stays available, because the
 * proposal is still real and a person who can see where it came from can still
 * make it; what they no longer get is a wall of engine keys standing in for an
 * explanation. If a kind lands here often enough to matter, it wants a card of
 * its own, the way a capture now has one below. */
function GenericProposed({ proposed }: { proposed: Record<string, unknown> }) {
  const title = typeof proposed['title'] === 'string' && proposed['title'] ? (proposed['title'] as string) : null;
  const rest = Object.entries(proposed).filter(
    ([k, v]) =>
      k !== 'title' && v !== null && v !== undefined && v !== '' && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
  );
  if (!title && rest.length === 0) {
    return <div class="review__triage-p">This one is a kind this screen cannot describe yet — open it where it came from, or decline it.</div>;
  }
  return (
    <div class="review__triage-summary">
      {title ? <div class="review__triage-h">{title}</div> : null}
      {rest.map(([k, v]) => (
        <div class="review__triage-p" key={k}>
          {k.replace(/_/g, ' ')}: {String(v)}
        </div>
      ))}
    </div>
  );
}

/** ⚑ A CAPTURE, IN WORDS — the Day 2 walk's S3.3 blocker, and the product's
 * first impression after "forward it to us".
 *
 * Every forwarded document lands here. It was rendered through GenericProposed
 * above, so a parent read four engine keys — `actions`, `context`, `sourceIds`,
 * `captureKind` — and the one sentence they were owed sat mid-dump behind a
 * `summary:` label.
 *
 * Three things, in the order a person asks them: what was read, where it came
 * from, and what it will become. The words are lib/proposals.ts's, which is
 * where Mail and Today read the same proposal from — one proposal must not
 * have two vocabularies. */
function CaptureProposed({ p }: { p: Proposal }) {
  const headline = proposalSummaryLine(p);
  const from = captureProvenanceLine(p);
  const becomes = captureOutcomeLines(p);
  const excerpt = captureExcerpt(p);
  // ⚑ SAID BEFORE "what it becomes", because it is the answer to that
  // question. A document nothing has read has no reading to show, and a card
  // that goes straight to an outcome list on one is the exact silence the Day
  // 3 walk found: sixty-three steps of captures that looked read and were not.
  const unread = captureUnreadLine(p);
  return (
    <div class="review__capture">
      <div class="review__capture-say">{headline}</div>
      {unread ? (
        <div class="review__capture-unread" data-testid="capture-unread">
          {unread}
        </div>
      ) : null}
      {from ? <div class="review__capture-from">{from}</div> : null}
      {excerpt ? <div class="review__capture-excerpt">{excerpt}</div> : null}
      {becomes.length > 0 ? (
        <>
          <div class="review__capture-label">what it becomes</div>
          <ul class="review__capture-becomes">
            {becomes.map((line, i) => (
              <li key={`${i}-${line}`}>{line}</li>
            ))}
          </ul>
        </>
      ) : (
        <div class="review__capture-becomes-none">Nothing will be created — approving files the original and no more.</div>
      )}
    </div>
  );
}

/** ⚑ B16 §4 — A COMMITMENT, IN WORDS. Before this a commitment (or its
 * closure) fell through to `GenericProposed` and printed its own raw keys —
 * "direction: owed_by_me", "riskLevel: low", "counterpartyName: Dana Okafor",
 * "dueAt: 2026-10-02T17:00:00-04:00" — the exact machine-dump this file's
 * capture card exists to prevent, on the household's own money-and-favours
 * ledger. `riskLevel` is never printed here at all: it is the extraction's
 * OWN confidence signal, not a fact about what is owed. */
function CommitmentProposed({ p }: { p: Proposal }) {
  const proposed = p.proposed as { title?: unknown; direction?: unknown; counterpartyName?: unknown; dueAt?: unknown };
  const title = typeof proposed.title === 'string' && proposed.title.trim() ? proposed.title.trim() : null;
  const counterpartyName =
    typeof proposed.counterpartyName === 'string' && proposed.counterpartyName.trim()
      ? proposed.counterpartyName.trim()
      : null;
  // Lenient fallback to 'owed_by_me' mirrors the materializer's own
  // (commitments.ts) — an extracted candidate always carries a direction in
  // practice, and a defensive default here must agree with the one approving
  // will actually write, or the card and the record it creates would say two
  // different things about the same commitment.
  const owedToMe = proposed.direction === 'owed_to_me';
  const who = owedToMe
    ? counterpartyName
      ? `${counterpartyName} owes you`
      : 'Owed to you'
    : counterpartyName
      ? `You owe ${counterpartyName}`
      : 'You owe this';
  return (
    <div class="review__triage-summary">
      <div class="review__triage-h">{who}</div>
      {title ? <div class="review__triage-p">{title}</div> : null}
      <div class="review__triage-p">{commitmentDueLine(proposed.dueAt)}</div>
    </div>
  );
}

/** Chunk PRG-W, plate "Plan" (03): the programme proposal card's own
 * reading — objective and measures, who does what, the cadence, the
 * guards, what is not ready yet, "This yes does / It does not". The shape
 * this reads is this file's own header note: an assumption about the
 * server's `proposed` jsonb, named there. */
function programmeFields(p: Proposal): ProgrammeProposedApi {
  return p.proposed as unknown as ProgrammeProposedApi;
}

function ProgrammeProposed({ p }: { p: Proposal }) {
  const fields = programmeFields(p);
  return (
    // E — its own class, deliberately NOT review__triage-summary: that class
    // carries a max-height/overflow-y meant for a long triage read, and it
    // clipped the does/doesNot block out of sight above Approve. A programme
    // card shows its full content, with no inner scrollbox.
    <div class="review__programme-proposed">
      <div class="review__triage-h">{fields.title}</div>
      <div class="review__triage-p">
        <b>Objective and measures.</b> {fields.objective} {fields.measuresSummary}
      </div>
      <div class="review__triage-p">
        <b>Who does what.</b> {fields.whoDoesWhat}
      </div>
      {fields.cadenceSummary.length > 0 ? (
        <div class="review__triage-p">
          <b>The cadence · {fields.cadenceSummary.length} standing obligations, each on its own clock.</b>
          <ul class="review__programme-cadence">
            {fields.cadenceSummary.map((step, i) => (
              <li key={i}>
                {step.day}
                {step.time ? ` ${step.time}` : ''} · {step.title} · gate: {step.gate}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {fields.guards.length > 0 ? (
        <div class="review__triage-p">
          <b>Guards.</b> {fields.guards.join(' · ')}
        </div>
      ) : null}
      {fields.notYetReady.length > 0 ? (
        <div class="review__triage-p">
          <b>Not ready yet, named honestly.</b> {fields.notYetReady.join(' · ')}
        </div>
      ) : null}
      <div class="review__programme-does">
        <div>
          <b>This yes does:</b> {fields.does}
        </div>
        <div>
          <b>It does not:</b> {fields.doesNot}
        </div>
      </div>
    </div>
  );
}

function draftFields(p: Proposal): { to: string; subject: string; body: string } {
  const proposed = p.proposed as { to?: Array<{ email: string }>; subject?: string; body?: string };
  return {
    to: (proposed.to ?? []).map((t) => t.email).join(', '),
    subject: proposed.subject ?? '',
    body: proposed.body ?? '',
  };
}

const EXCLUSION_REASON_LABEL: Record<string, string> = {
  no_consent: 'no consent on file',
  locked: 'unsubscribed — locked until they lift it',
  suppressed: 'this address asked not to be mailed',
  no_address: 'no address on file',
  no_live_party: 'nobody on record to address',
};

// GAP 63 — the Review card's own read of a `segment_broadcast` proposal:
// everything amendment 6 asks for (counts by reason, the rendered sample,
// channel + sending identity) pulled straight from what `createSegment
// BroadcastProposal` stored on `proposed` — the screen renders what was
// actually computed, never a re-derived guess.
function broadcastFields(p: Proposal): {
  channel: string;
  sendingIdentityName: string | null;
  totalMatched: number;
  eligible: number;
  excludedByReason: Record<string, number>;
  sample: { to: string; subject: string; body: string; unsubscribeUrl: string | null } | null;
} {
  const proposed = p.proposed as {
    channel?: string;
    sendingIdentityName?: string | null;
    totalMatched?: number;
    eligible?: number;
    excludedByReason?: Record<string, number>;
    sample?: { to: string; subject: string; body: string; unsubscribeUrl: string | null } | null;
  };
  return {
    channel: proposed.channel ?? 'email',
    sendingIdentityName: proposed.sendingIdentityName ?? null,
    totalMatched: proposed.totalMatched ?? 0,
    eligible: proposed.eligible ?? 0,
    excludedByReason: proposed.excludedByReason ?? {},
    sample: proposed.sample ?? null,
  };
}

export function Review({ tenantId, navigate }: ReviewProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [pending, setPending] = useState<Proposal[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [cardError, setCardError] = useState<Record<string, string>>({});
  // PC2 design §7 — the batch filter. 'all' shows everything, unchanged.
  const [kindFilter, setKindFilter] = useState<'all' | 'post_document'>('all');
  // PC3 — the account picker's own source, loaded once; and per-proposal
  // choices (which accounts, what schedule).
  const [accounts, setAccounts] = useState<ConnectedAccountApi[]>([]);
  const [chosenAccounts, setChosenAccounts] = useState<Record<string, string[]>>({});
  // Chunk PRG-W — "Not now" on a programme card asks why before it declines
  // (the server's own contract: POST /decline {reason}); this id is the
  // proposal currently showing that reason box.
  const [decliningProgramme, setDecliningProgramme] = useState<string | null>(null);

  function toggleAccount(proposalId: string, accountId: string) {
    setChosenAccounts((cur) => {
      const current = cur[proposalId] ?? [];
      const next = current.includes(accountId) ? current.filter((id) => id !== accountId) : [...current, accountId];
      return { ...cur, [proposalId]: next };
    });
  }

  function refresh() {
    return listProposals(tenantId, 'pending')
      .then((proposals) => {
        setPending(proposals);
        setLoad({ phase: 'ready' });
      })
      .catch((err: unknown) => {
        setLoad({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'the queue could not be read',
        });
      });
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh();
    // PC3 — the picker's source, loaded alongside the queue; a failure
    // here just leaves the picker empty (a post can still be approved with
    // no accounts chosen, PC2's own behaviour), never blocking the queue.
    listPublishAccounts(tenantId)
      .then(setAccounts)
      .catch(() => setAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  function settle(p: Proposal, line: string, undo?: Receipt['undo']) {
    setPending((cur) => cur.filter((x) => x.id !== p.id));
    setReceipts((cur) => [{ id: p.id, line, undo }, ...cur].slice(0, 8));
  }

  // The assistant's change to a Google event: Huan's yes AND his answer to
  // "Tell the guests?" in one tap — Google needs it at the moment of the write.
  async function approveGoogleChange(p: Proposal, tellGuests: boolean | undefined) {
    setBusy(p.id);
    setCardError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      const { event } = await approveGoogleEventChange(tenantId, p.id, tellGuests);
      settle(p, event.receipt, event.outcome === 'done' ? { changeId: event.changeId, guests: event.guests.length } : undefined);
    } catch (err) {
      fail(p, err);
    } finally {
      setBusy(null);
    }
  }

  async function undoReceipt(r: Receipt, tellGuests: boolean | undefined) {
    if (!r.undo) return;
    setBusy(r.id);
    try {
      const { event } = await undoGoogleEventChange(tenantId, r.undo.changeId, tellGuests);
      setReceipts((cur) => cur.map((x) => (x.id === r.id ? { id: r.id, line: event.receipt } : x)));
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'the undo did not happen';
      setReceipts((cur) => cur.map((x) => (x.id === r.id ? { ...x, line: `${x.line} — Undo refused: ${message}` } : x)));
    } finally {
      setBusy(null);
    }
  }

  function fail(p: Proposal, err: unknown) {
    const message =
      err instanceof ApiError && err.code === 'mail_not_configured'
        ? 'This instance cannot send yet — the draft stays here until its mailbox is switched on.'
        : err instanceof ApiError && err.code === 'record_failed'
          ? 'Sent — the thread record is catching up.'
          : err instanceof ApiError ? err.message : 'the decision was not recorded';
    if (err instanceof ApiError && err.code === 'record_failed') {
      settle(p, `Sent — ${draftFields(p).subject}`);
      return;
    }
    setCardError((cur) => ({ ...cur, [p.id]: message }));
  }

  /** Publish PC2 — the failures a `post_document` candidate can hit (kill
   * memory, the rail, the lint, the similarity guard) carry their own
   * server sentence and a `failures`/`nearDuplicates` list on the error
   * body; this reads all of them into one line rather than falling back to
   * the generic "N sentence(s) refused" the rail's own message would show
   * alone. */
  function failPost(p: Proposal, err: unknown) {
    if (err instanceof ApiError) {
      const body = err.body as { error?: { failures?: Array<{ sentence?: string; reason?: string; pattern?: string }> } } | undefined;
      const failures = body?.error?.failures;
      if ((err.code === 'rail_refused' || err.code === 'lint_refused') && failures && failures.length > 0) {
        setCardError((cur) => ({
          ...cur,
          [p.id]: failures.map((f) => `“${f.sentence}” — ${f.reason ?? f.pattern ?? 'refused'}`).join(' · '),
        }));
        return;
      }
      if (err.code === 'candidate_killed' || err.code === 'similarity_refused') {
        setCardError((cur) => ({ ...cur, [p.id]: err.message }));
        return;
      }
    }
    fail(p, err);
  }

  async function approvePost(p: Proposal) {
    setBusy(p.id);
    setCardError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      const accountIds = chosenAccounts[p.id] ?? [];
      const schedule = (edits[`${p.id}:schedule`] ?? '').trim();
      const r = await approvePostDocument(tenantId, p.id, {
        ...(accountIds.length > 0 ? { accountIds } : {}),
        ...(schedule ? { notBefore: new Date(schedule).toISOString() } : {}),
      });
      settle(p, publishApproveReceipt(r.post.channel, r.post.recordDisplayName, r.post.deliveries));
    } catch (err) {
      failPost(p, err);
    } finally {
      setBusy(null);
    }
  }

  async function killPost(p: Proposal) {
    const reason = (edits[`${p.id}:kill`] ?? '').trim();
    if (!reason) return;
    setBusy(p.id);
    setCardError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      await killPostDocument(tenantId, p.id, reason);
      settle(p, `Killed — ${postFields(p).channel}: ${reason}`);
    } catch (err) {
      failPost(p, err);
    } finally {
      setBusy(null);
    }
  }

  /** design §2's "Edit": Save re-POSTs the edited text as a NEW candidate
   * (same citations, sentences re-split at sentence boundaries, the rail
   * runs again on the new candidate) and rejects the OLD proposal with
   * `edited → <new proposal id>`. An edit that fails the rail is shown on
   * THIS card (failPost), and the old proposal is left exactly as it was —
   * nothing about it changes until a new candidate actually clears. */
  async function saveEditedPost(p: Proposal) {
    const post = postFields(p);
    const editedText = (edits[p.id] ?? post.text).trim();
    setBusy(p.id);
    setCardError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      const sentences = resplitEditedSentences(post.sentences, editedText);
      const created = await createPublishPost(tenantId, {
        recordId: post.subject.record_id,
        channel: post.channel,
        text: editedText,
        sentences,
        citations: post.citations.map((c) => ({
          tier: c.tier,
          source_kind: c.source_kind,
          source_ref: c.source_ref,
          span: c.span,
          captured_at: c.captured_at,
          expires_at: c.expires_at,
          permission: c.permission,
          jurisdiction: c.jurisdiction,
        })),
        disclosure: post.disclosure,
        ai_visual: post.ai_visual,
        minors_flag: post.minors_flag,
        ...(p.rationale ? { why: p.rationale } : {}),
      });
      await rejectProposal(tenantId, p.id, `edited → ${created.proposal.id}`);
      settle(p, `Saved as a new candidate for ${post.channel} — it is back in this queue`);
    } catch (err) {
      failPost(p, err);
    } finally {
      setBusy(null);
    }
  }

  async function sendDraft(p: Proposal) {
    setBusy(p.id);
    setCardError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      const body = edits[p.id] ?? draftFields(p).body;
      const r = await approveEmailDraft(tenantId, p.id, { body });
      settle(p, `Sent to ${draftFields(p).to} — ${draftFields(p).subject}${r.sent.edited ? ' (with your edits)' : ''}`);
    } catch (err) {
      fail(p, err);
    } finally {
      setBusy(null);
    }
  }

  // B18a: deciding an enquiry. The fork travels WITH the decision, so the
  // reclassify is the same one tap as the approve — not a mode you switch
  // into first. `forks[p.id]` holds the chosen fork until then; absent, the
  // model's own proposal stands.
  async function decideTriage(p: Proposal, fork: 'invite' | 'needs_more') {
    const t = triageFields(p);
    setBusy(p.id);
    setCardError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      const body = edits[p.id] ?? t.draftReplyMd;
      const r = await approveIntakeTriage(tenantId, p.id, { fork, body });
      settle(
        p,
        `Sent to ${r.triage.to} — ${r.triage.fork === 'invite' ? 'invited' : 'asked for more'}` +
        `${r.triage.reclassified ? ' (you changed the call)' : ''}` +
        `${r.triage.detailsLinkIssued ? ', with a link to add to it' : ''}`
      );
    } catch (err) {
      fail(p, err);
    } finally {
      setBusy(null);
    }
  }

  // B18e: authorizing a client's message into work. The word travels WITH
  // the decision — passing the other one is the reclassify, the same single
  // tap — and when the model had no word, neither button is the default:
  // both are offered and one must be chosen.
  async function decideWork(p: Proposal, classification: 'bug' | 'enhancement') {
    const w = workFields(p);
    setBusy(p.id);
    setCardError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      const title = edits[p.id] ?? w.title;
      const r = await approveClientWork(tenantId, p.id, { classification, title });
      settle(
        p,
        `On your to-dos as ${r.work.classification === 'bug' ? 'a bug' : 'an enhancement'} — ` +
        `${r.work.title}${r.work.edited ? ' (with your edits)' : ''}`
      );
    } catch (err) {
      fail(p, err);
    } finally {
      setBusy(null);
    }
  }

  // B18f — sending the letter. Three fields, all editable in place, because
  // what gets stored is what Huan approved and a client reads exactly that.
  // `edits` already holds the body (every card uses it); timing and billing
  // get their own keys off the same map.
  async function sendLetter(p: Proposal) {
    const l = letterFields(p);
    setBusy(p.id);
    setCardError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      // B18g — the window travels with the decision. An EMPTY field is a
      // real answer ("no dates promised"), so it is sent as null rather than
      // omitted: omitting it would keep whatever the lever prefilled, which
      // is the opposite of what clearing it means.
      const start = edits[`${p.id}:start`] ?? l.promisedStart ?? '';
      const end = edits[`${p.id}:end`] ?? l.promisedEnd ?? '';
      const r = await approveClientLetter(tenantId, p.id, {
        bodyMd: edits[p.id] ?? l.bodyMd,
        ...(edits[`${p.id}:timing`] === undefined ? {} : { timingMd: edits[`${p.id}:timing`]! }),
        ...(edits[`${p.id}:billing`] === undefined ? {} : { billingMd: edits[`${p.id}:billing`]! }),
        promisedStart: start.trim() || null,
        promisedEnd: end.trim() || null,
      });
      settle(
        p,
        `Sent to ${l.partyName} — ${r.letter.itemCount} item${r.letter.itemCount === 1 ? '' : 's'}` +
        `${r.letter.edited ? ' (with your edits)' : ''}` +
        // The mail is the nudge, not the delivery. Saying so keeps a failed
        // nudge from reading as a failed send.
        `${r.letter.mailFailed ? ' — on their portal, but the email did not go' : ''}`
      );
    } catch (err) {
      fail(p, err);
    } finally {
      setBusy(null);
    }
  }

  async function approveGeneric(p: Proposal) {
    setBusy(p.id);
    setCardError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      await approveProposal(tenantId, p.id);
      settle(p, `Approved — ${kindLabel(p.targetType).toLowerCase()}`);
    } catch (err) {
      fail(p, err);
    } finally {
      setBusy(null);
    }
  }

  async function decline(p: Proposal) {
    setBusy(p.id);
    setCardError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      await rejectProposal(tenantId, p.id, 'declined from Review');
      settle(
        p,
        p.targetType === 'email_draft' || p.targetType === 'intake_triage'
          ? 'Discarded — nothing was sent'
          : `Declined — ${kindLabel(p.targetType).toLowerCase()}`
      );
    } catch (err) {
      fail(p, err);
    } finally {
      setBusy(null);
    }
  }

  // Chunk PRG-W — the programme card's own doors. Approve/decline the
  // PROGRAMME (POST /api/programmes/:id/approve|decline), never the
  // generic proposal endpoint: the server's own state machine lives on
  // the engagement record, and the proposal row is its receipt.
  async function approveProgrammeCard(p: Proposal) {
    if (!p.targetId) {
      setCardError((cur) => ({ ...cur, [p.id]: 'This proposal carries no programme to approve.' }));
      return;
    }
    setBusy(p.id);
    setCardError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      const { receipt } = await approveProgramme(tenantId, p.targetId);
      settle(p, receipt);
    } catch (err) {
      fail(p, err);
    } finally {
      setBusy(null);
    }
  }

  async function declineProgrammeCard(p: Proposal) {
    if (!p.targetId) return;
    const reason = (edits[`${p.id}:programme-decline`] ?? '').trim();
    if (!reason) return;
    setBusy(p.id);
    setCardError((cur) => ({ ...cur, [p.id]: '' }));
    try {
      const { receipt } = await declineProgramme(tenantId, p.targetId, reason);
      settle(p, receipt);
      setDecliningProgramme(null);
    } catch (err) {
      fail(p, err);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div class="review">
      <div class="review__eyebrow">Review</div>

      {load.phase === 'loading' ? (
        <div class="review__skeleton" aria-hidden="true">
          <div class="review__skeleton-row" />
          <div class="review__skeleton-row" />
        </div>
      ) : load.phase === 'error' ? (
        <div class="review__say">
          The queue could not be read.
          <br />
          <span class="review__say-soft">{load.message}</span>
        </div>
      ) : pending.length === 0 && receipts.length === 0 ? (
        <div class="review__say">
          Nothing awaits your decision.
          <br />
          <span class="review__say-soft">
            Drafted replies, extracted commitments and captures queue here —
            nothing machine-made takes effect until you say so.
          </span>
        </div>
      ) : (
        <>
          {/* PC2 design §7 — the batch filter. Only worth showing once
              there is more than one kind of thing to sort between. */}
          {pending.some((p) => p.targetType === 'post_document') ? (
            <div class="review__filters">
              <button
                type="button"
                class={`review__filter${kindFilter === 'all' ? ' is-active' : ''}`}
                onClick={() => setKindFilter('all')}
              >
                All
              </button>
              <button
                type="button"
                class={`review__filter${kindFilter === 'post_document' ? ' is-active' : ''}`}
                onClick={() => setKindFilter('post_document')}
              >
                Posts
              </button>
            </div>
          ) : null}
          <div class="review__list">
            {pending
              .filter((p) => kindFilter === 'all' || p.targetType === kindFilter)
              .map((p) => {
              const isDraft = p.targetType === 'email_draft';
              const isTriage = p.targetType === 'intake_triage';
              const isWork = p.targetType === 'client_work';
              const isLetter = p.targetType === 'client_letter';
              const isPost = p.targetType === 'post_document';
              const isCapture = p.targetType === 'actor_capture';
              // B16 §4 — target_type is 'commitment' for BOTH an extracted
              // candidate and a closure detection (commitments.ts's own
              // INSERTs); `kind` is the one field that tells them apart, so
              // it is checked too rather than assumed dead by construction.
              const isCommitment = p.targetType === 'commitment' || p.targetType === 'commitment_close' || p.kind === 'commitment_close';
              // Chunk PRG-W — a programme proposal, drafted from a plan.
              const isProgramme = p.targetType === 'programme';
              // GAP 63 — a segment broadcast.
              const isBroadcast = p.targetType === 'segment_broadcast';
              const ge = p.targetType === 'google_event_change' ? googleEventChangeFields(p) : null;
              const ask = p.targetType === 'google_event_organizer_ask' ? organizerAskFields(p) : null;
              const d = isDraft ? draftFields(p) : null;
              const t = isTriage ? triageFields(p) : null;
              const w = isWork ? workFields(p) : null;
              const lt = isLetter ? letterFields(p) : null;
              const bc = isBroadcast ? broadcastFields(p) : null;
              const post = isPost ? postFields(p) : null;
              return (
                <div class="review__item" key={p.id}>
                  <div class="review__item-kind">
                    {isPost && post ? `Post · ${post.channel}` : kindLabel(p.targetType)}
                  </div>
                  {ge ? (
                    <>
                      <div class="review__triage-who">
                        <span class="review__triage-name">
                          {ge.op === 'cancel' ? 'Cancel' : 'Change'} “{ge.title}”
                        </span>
                        <span class="review__triage-confidence">on {ge.calendar}</span>
                      </div>
                      {ge.lines.length ? (
                        <ul class="review__letter-items">
                          {ge.lines.map((l) => (
                            <li class="review__letter-item" key={l}>{l}</li>
                          ))}
                        </ul>
                      ) : null}
                      {ge.guests.length ? (
                        <div class="review__triage-from">
                          Reaches {ge.guests.length} guest{ge.guests.length === 1 ? '' : 's'}: {ge.guests.join(', ')}
                        </div>
                      ) : null}
                      <div class="review__actions">
                        {ge.guests.length ? (
                          <>
                            <button
                              type="button"
                              class="review__btn review__btn--primary"
                              disabled={busy === p.id}
                              onClick={() => void approveGoogleChange(p, true)}
                            >
                              {ge.op === 'cancel' ? 'Cancel it and tell them' : 'Change it and tell them'}
                            </button>
                            <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => void approveGoogleChange(p, false)}>
                              {ge.op === 'cancel' ? 'Cancel it, don’t tell them' : 'Change it, don’t tell them'}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            class="review__btn review__btn--primary"
                            disabled={busy === p.id}
                            onClick={() => void approveGoogleChange(p, undefined)}
                          >
                            {ge.op === 'cancel' ? 'Cancel it' : 'Change it'}
                          </button>
                        )}
                        <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => void decline(p)}>
                          {ge.op === 'cancel' ? 'Keep it' : 'Leave it'}
                        </button>
                      </div>
                    </>
                  ) : ask ? (
                    <>
                      <div class="review__triage-who">
                        <span class="review__triage-name">To {ask.to}</span>
                      </div>
                      <div class="review__triage-from">{ask.subject}</div>
                      <pre class="review__draft-body">{ask.body}</pre>
                      <div class="review__actions">
                        <button
                          type="button"
                          class="review__btn review__btn--primary"
                          onClick={() => void navigator.clipboard?.writeText(`${ask.subject}\n\n${ask.body}`)}
                        >
                          Copy the note
                        </button>
                        <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => void decline(p)}>
                          Discard
                        </button>
                      </div>
                    </>
                  ) : isTriage && t ? (
                    <>
                      <div class="review__triage-who">
                        <span class="review__triage-name">{t.businessName ?? t.email ?? 'an unnamed enquiry'}</span>
                        <span class="review__triage-confidence">
                          {TRIAGE_CONFIDENCE_LABEL[t.confidence] ?? t.confidence}
                        </span>
                      </div>
                      {/* The address, unless it is already standing in as the
                          name — printing it twice reads as two facts. */}
                      {t.email && t.businessName ? <div class="review__triage-from">{t.email}</div> : null}

                      {/* The proposal, in words, before either button — a
                          fork with no stated reason is an opinion nobody
                          can check. */}
                      <div class="review__triage-fork">
                        <span class={`review__triage-call${t.fork === 'needs_more' ? ' is-more' : ''}`}>
                          {t.fork === 'invite' ? 'enough to start' : 'ask before starting'}
                        </span>
                        <span class="review__triage-why">{t.forkReason}</span>
                      </div>

                      <SummaryBlock md={t.summaryMd} />

                      <textarea
                        class="review__draft-body"
                        rows={8}
                        value={edits[p.id] ?? t.draftReplyMd}
                        onInput={(e) => setEdits((cur) => ({ ...cur, [p.id]: (e.target as HTMLTextAreaElement).value }))}
                      />

                      {/* Both forks are buttons, and BOTH SEND. The one the
                          model proposed leads; the other is the reclassify,
                          which is the same single tap rather than a mode. */}
                      <div class="review__actions">
                        <button
                          type="button"
                          class="review__btn review__btn--primary"
                          disabled={busy === p.id}
                          onClick={() => void decideTriage(p, t.fork)}
                        >
                          {t.fork === 'invite' ? 'Invite them' : 'Ask for more'}
                        </button>
                        <button
                          type="button"
                          class="review__btn"
                          disabled={busy === p.id}
                          onClick={() => void decideTriage(p, t.fork === 'invite' ? 'needs_more' : 'invite')}
                        >
                          {t.fork === 'invite' ? 'Ask for more instead' : 'Invite them instead'}
                        </button>
                        <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => void decline(p)}>
                          Discard
                        </button>
                      </div>
                    </>
                  ) : isLetter && lt ? (
                    <>
                      <div class="review__triage-who">
                        <span class="review__triage-name">{lt.partyName}</span>
                        <span class="review__triage-confidence">
                          {lt.items.length} item{lt.items.length === 1 ? '' : 's'}
                        </span>
                      </div>

                      {/* WHAT IS BEING PROMISED, before the prose about it —
                          these are the words the client will hold us to, and
                          they are FROZEN the moment this is sent. */}
                      <ol class="review__letter-items">
                        {lt.items.map((i) => (
                          <li class="review__letter-item" key={i.taskId}>{i.title}</li>
                        ))}
                      </ol>

                      <textarea
                        class="review__draft-body"
                        rows={8}
                        value={edits[p.id] ?? lt.bodyMd}
                        onInput={(e) => setEdits((cur) => ({ ...cur, [p.id]: (e.target as HTMLTextAreaElement).value }))}
                      />

                      <label class="review__letter-label">Timing</label>
                      <textarea
                        class="review__draft-body review__letter-line"
                        rows={2}
                        value={edits[`${p.id}:timing`] ?? lt.timingMd ?? ''}
                        onInput={(e) => setEdits((cur) => ({ ...cur, [`${p.id}:timing`]: (e.target as HTMLTextAreaElement).value }))}
                      />

                      <label class="review__letter-label">Billing</label>
                      <textarea
                        class="review__draft-body review__letter-line"
                        rows={2}
                        value={edits[`${p.id}:billing`] ?? lt.billingMd ?? ''}
                        onInput={(e) => setEdits((cur) => ({ ...cur, [`${p.id}:billing`]: (e.target as HTMLTextAreaElement).value }))}
                      />

                      {/* ⚑ B18g — THE PROMISED WINDOW, in dates. Prefilled,
                          editable, and CLEARABLE: emptying both means no time
                          is held, which is the honest answer when the timing
                          is genuinely unknown. Nothing is ever read out of
                          the timing sentence above. */}
                      <label class="review__letter-label">Time held for this</label>
                      <div class="review__letter-window">
                        <input
                          class="review__letter-date"
                          type="date"
                          value={edits[`${p.id}:start`] ?? lt.promisedStart ?? ''}
                          onInput={(e) => setEdits((cur) => ({ ...cur, [`${p.id}:start`]: (e.target as HTMLInputElement).value }))}
                        />
                        <span class="review__letter-to">to</span>
                        <input
                          class="review__letter-date"
                          type="date"
                          value={edits[`${p.id}:end`] ?? lt.promisedEnd ?? ''}
                          onInput={(e) => setEdits((cur) => ({ ...cur, [`${p.id}:end`]: (e.target as HTMLInputElement).value }))}
                        />
                      </div>
                      <div class="review__letter-hint">
                        With both dates empty, nothing is held — no dates are promised.
                      </div>

                      <div class="review__actions">
                        <button
                          type="button"
                          class="review__btn review__btn--primary"
                          disabled={busy === p.id}
                          onClick={() => void sendLetter(p)}
                        >
                          Send it
                        </button>
                        <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => void decline(p)}>
                          Not yet
                        </button>
                      </div>
                    </>
                  ) : isWork && w ? (
                    <>
                      <div class="review__triage-who">
                        <span class="review__triage-name">{w.partyName}</span>
                        <span class="review__triage-confidence">{w.bucketName}</span>
                      </div>

                      {/* THEIR WORDS FIRST. The title below is our reading of
                          them, and a reading shown without its source is a
                          claim nobody can check. */}
                      {w.quote ? <div class="review__work-quote">“{w.quote}”</div> : null}

                      {/* The title is editable in place — it is what lands on
                          To-dos, so it is written here, not renamed later. */}
                      <textarea
                        class="review__draft-body review__work-title"
                        rows={2}
                        value={edits[p.id] ?? w.title}
                        onInput={(e) => setEdits((cur) => ({ ...cur, [p.id]: (e.target as HTMLTextAreaElement).value }))}
                      />

                      {w.rationale ? <div class="review__item-rationale">{w.rationale}</div> : null}
                      {/* The model disagreeing does not stop the card — it is
                          said, beside the buttons, and Huan decides. */}
                      {w.readsAsWork === false ? (
                        <div class="review__work-doubt">This reads more like a question than work.</div>
                      ) : null}
                      {w.classification === null ? (
                        <div class="review__work-doubt">No call on this one — say which it is.</div>
                      ) : null}

                      <div class="review__actions">
                        {(w.classification === 'enhancement'
                          ? (['enhancement', 'bug'] as const)
                          : (['bug', 'enhancement'] as const)
                        ).map((word, i) => (
                          <button
                            key={word}
                            type="button"
                            class={`review__btn${i === 0 && w.classification ? ' review__btn--primary' : ''}`}
                            disabled={busy === p.id}
                            onClick={() => void decideWork(p, word)}
                          >
                            {word === 'bug' ? 'File as a bug' : 'File as an enhancement'}
                          </button>
                        ))}
                        <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => void decline(p)}>
                          Not work
                        </button>
                      </div>
                    </>
                  ) : isDraft && d ? (
                    <>
                      <div class="review__draft-envelope">
                        <span class="review__draft-to">to {d.to}</span>
                        <span class="review__draft-subject">{d.subject}</span>
                      </div>
                      <textarea
                        class="review__draft-body"
                        rows={7}
                        value={edits[p.id] ?? d.body}
                        onInput={(e) => setEdits((cur) => ({ ...cur, [p.id]: (e.target as HTMLTextAreaElement).value }))}
                      />
                      {p.rationale ? <div class="review__item-rationale">{p.rationale}</div> : null}
                      <div class="review__actions">
                        <button type="button" class="review__btn review__btn--primary" disabled={busy === p.id} onClick={() => void sendDraft(p)}>
                          Send
                        </button>
                        <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => void decline(p)}>
                          Discard
                        </button>
                      </div>
                    </>
                  ) : isPost && post ? (
                    <>
                      <div class="review__post-about">{post.subject.record_display_name}</div>

                      <textarea
                        class="review__draft-body"
                        rows={8}
                        value={edits[p.id] ?? post.text}
                        onInput={(e) => setEdits((cur) => ({ ...cur, [p.id]: (e.target as HTMLTextAreaElement).value }))}
                      />

                      <div class="review__post-sentences">
                        {post.sentences.map((s, i) => {
                          const cites = citationsForSentence(s, post.citations);
                          return (
                            <div class="review__post-sentence" key={i}>
                              <span class="review__post-tier">{s.tier}</span>
                              <span class="review__post-sentence-text">{s.text}</span>
                              {cites.map((c, ci) => (
                                <div class="review__post-cite" key={ci}>
                                  <span class="review__post-cite-meta">
                                    {c.source_kind}
                                    {c.captured_at ? ` · captured ${new Date(c.captured_at).toLocaleDateString()}` : ''}
                                  </span>
                                  {c.span ? (
                                    <span class="review__post-cite-span">
                                      {c.span}
                                      {s.paraphrase ? ' (paraphrase)' : ''}
                                    </span>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>

                      <div class="review__post-compliance">
                        {post.disclosure !== 'none' ? (
                          <span class="review__post-tag">{post.disclosure === 'paid' ? '#ad' : post.disclosure}</span>
                        ) : null}
                        {post.ai_visual ? <span class="review__post-tag">AI visual — platform AI label required</span> : null}
                        {post.minors_flag ? <span class="review__post-tag">minor — guardian approval required</span> : null}
                      </div>

                      <div class="review__post-similarity">
                        {post.similarity.near_duplicates_30d > 0
                          ? `${post.similarity.near_duplicates_30d} near-duplicate${post.similarity.near_duplicates_30d === 1 ? '' : 's'} in 30 days`
                          : 'no near-duplicates in 30 days'}
                      </div>

                      {post.lint.personal_attributes.length > 0 ? (
                        <div class="review__post-lint">
                          reads like it addresses a personal attribute: “{post.lint.personal_attributes[0]}”
                        </div>
                      ) : null}

                      {post.subject.consent === 'given' ? (
                        <div class="review__post-consent">consent from {post.subject.subject_name ?? 'the subject'} is on file</div>
                      ) : null}

                      {/* PC3 design §6 — the account picker. A
                          sandbox_private account is selectable; the row
                          says it will fail rather than hiding it. */}
                      {accounts.length > 0 ? (
                        <div class="review__post-accounts">
                          {accounts
                            .filter((a) => a.platform === post.channel && a.status !== 'revoked')
                            .map((a) => (
                              <label class="review__post-account" key={a.id}>
                                <input
                                  type="checkbox"
                                  checked={(chosenAccounts[p.id] ?? []).includes(a.id)}
                                  onChange={() => toggleAccount(p.id, a.id)}
                                />
                                <span>{a.handle}</span>
                                {a.status === 'sandbox_private' ? (
                                  <span class="review__post-account-warn">will fail — not audited for public posting</span>
                                ) : null}
                              </label>
                            ))}
                          <input
                            class="review__post-schedule"
                            type="datetime-local"
                            value={edits[`${p.id}:schedule`] ?? ''}
                            onInput={(e) => setEdits((cur) => ({ ...cur, [`${p.id}:schedule`]: (e.target as HTMLInputElement).value }))}
                          />
                        </div>
                      ) : null}

                      <div class="review__actions">
                        {post.subject.consent === 'missing' || post.subject.consent === 'expired' ? (
                          <div class="review__post-consent-block">
                            consent from {post.subject.subject_name ?? 'the subject'} is {post.subject.consent} — ask first
                          </div>
                        ) : (
                          <button
                            type="button"
                            class="review__btn review__btn--primary"
                            disabled={busy === p.id}
                            onClick={() => void approvePost(p)}
                          >
                            Approve
                          </button>
                        )}
                        <button
                          type="button"
                          class="review__btn"
                          disabled={busy === p.id}
                          onClick={() => void saveEditedPost(p)}
                        >
                          Save as new candidate
                        </button>
                      </div>
                      <div class="review__post-kill">
                        <input
                          class="review__post-kill-reason"
                          type="text"
                          placeholder="reason"
                          value={edits[`${p.id}:kill`] ?? ''}
                          onInput={(e) => setEdits((cur) => ({ ...cur, [`${p.id}:kill`]: (e.target as HTMLInputElement).value }))}
                        />
                        <button
                          type="button"
                          class="review__btn review__btn--kill"
                          disabled={busy === p.id || !(edits[`${p.id}:kill`] ?? '').trim()}
                          onClick={() => void killPost(p)}
                        >
                          Kill
                        </button>
                      </div>
                    </>
                  ) : isCapture ? (
                    <>
                      {/* ⚑ S3.3 — THE SCREEN EVERY FORWARDED DOCUMENT LANDS ON.
                          What was read, where it came from, what it becomes —
                          never the payload it is carried in. */}
                      <CaptureProposed p={p} />
                      {p.rationale ? <div class="review__item-rationale">{p.rationale}</div> : null}
                      <div class="review__actions">
                        <button type="button" class="review__btn review__btn--primary" disabled={busy === p.id} onClick={() => void approveGeneric(p)}>
                          Approve
                        </button>
                        <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => void decline(p)}>
                          Decline
                        </button>
                      </div>
                    </>
                  ) : isProgramme ? (
                    <>
                      {/* Chunk PRG-W, plate "Plan" (03): Approve the
                          programme · Edit (opens the step editor) · Not now
                          (decline — reasoned, never silent). */}
                      <ProgrammeProposed p={p} />
                      {decliningProgramme === p.id ? (
                        <div class="review__programme-decline">
                          <input
                            class="review__programme-decline-input"
                            placeholder="why not?"
                            value={edits[`${p.id}:programme-decline`] ?? ''}
                            onInput={(e) => setEdits((cur) => ({ ...cur, [`${p.id}:programme-decline`]: (e.target as HTMLInputElement).value }))}
                          />
                          <div class="review__actions">
                            <button
                              type="button"
                              class="review__btn review__btn--primary"
                              disabled={busy === p.id || !(edits[`${p.id}:programme-decline`] ?? '').trim()}
                              onClick={() => void declineProgrammeCard(p)}
                            >
                              Not now
                            </button>
                            <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => setDecliningProgramme(null)}>
                              Never mind
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div class="review__actions">
                          <button
                            type="button"
                            class="review__btn review__btn--primary"
                            disabled={busy === p.id}
                            onClick={() => void approveProgrammeCard(p)}
                          >
                            Approve the programme
                          </button>
                          <button
                            type="button"
                            class="review__btn"
                            disabled={busy === p.id || !p.targetId}
                            onClick={() => p.targetId && navigate?.(routePath({ screen: 'programmeDetail', id: p.targetId }))}
                          >
                            Edit
                          </button>
                          <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => setDecliningProgramme(p.id)}>
                            Not now
                          </button>
                        </div>
                      )}
                    </>
                  ) : isCommitment ? (
                    <>
                      {/* ⚑ B16 §4 — a household's own money-and-favours ledger,
                          never four raw engine keys about it. */}
                      <CommitmentProposed p={p} />
                      {p.rationale ? <div class="review__item-rationale">{p.rationale}</div> : null}
                      <div class="review__actions">
                        <button type="button" class="review__btn review__btn--primary" disabled={busy === p.id} onClick={() => void approveGeneric(p)}>
                          Approve
                        </button>
                        <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => void decline(p)}>
                          Decline
                        </button>
                      </div>
                    </>
                  ) : isBroadcast && bc ? (
                    <>
                      {/* GAP 63 amendment 6 — channel + sending identity,
                          counts by reason, then the rendered sample. Every
                          number here is what `createSegmentBroadcastProposal`
                          actually computed — never re-derived on this
                          screen. */}
                      <div class="review__triage-who">
                        <span class="review__triage-name">
                          {bc.channel} · {bc.sendingIdentityName ?? 'this business'}
                        </span>
                        <span class="review__triage-confidence">
                          {bc.eligible} of {bc.totalMatched} matched
                        </span>
                      </div>

                      {Object.keys(bc.excludedByReason).length > 0 ? (
                        <ul class="review__letter-items">
                          {Object.entries(bc.excludedByReason).map(([reason, count]) => (
                            <li class="review__letter-item" key={reason}>
                              {count} excluded — {EXCLUSION_REASON_LABEL[reason] ?? reason}
                            </li>
                          ))}
                        </ul>
                      ) : null}

                      {bc.sample ? (
                        <div class="review__draft-envelope">
                          <span class="review__draft-to">to {bc.sample.to}</span>
                          <span class="review__draft-subject">{bc.sample.subject}</span>
                        </div>
                      ) : null}
                      {bc.sample ? <div class="review__item-rationale">{bc.sample.body}</div> : null}
                      {bc.sample?.unsubscribeUrl ? (
                        <div class="review__letter-hint">Carries a working unsubscribe link: {bc.sample.unsubscribeUrl}</div>
                      ) : null}

                      {/* Receipts-not-acknowledgements (interaction doctrine):
                          what this yes actually does, stated plainly, before
                          the button that does it. */}
                      <div class="review__letter-hint">
                        <b>This yes does:</b> sends ONE email to each of the {bc.eligible} eligible recipient
                        {bc.eligible === 1 ? '' : 's'} above, through {bc.sendingIdentityName ?? "this business's"} own address, each
                        carrying its own working unsubscribe link.
                      </div>
                      <div class="review__letter-hint">
                        <b>It does not:</b> reach anyone excluded above, anyone who joins the segment after this approval, or any
                        child record's own address (a dancer's email/handle is never a recipient).
                      </div>

                      <div class="review__actions">
                        <button type="button" class="review__btn review__btn--primary" disabled={busy === p.id} onClick={() => void approveGeneric(p)}>
                          Approve
                        </button>
                        <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => void decline(p)}>
                          Not now
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* What is being approved, in full — Approve must never
                          commit content the screen did not show. */}
                      <GenericProposed proposed={p.proposed} />
                      {p.rationale ? <div class="review__item-rationale">{p.rationale}</div> : null}
                      <div class="review__actions">
                        <button type="button" class="review__btn review__btn--primary" disabled={busy === p.id} onClick={() => void approveGeneric(p)}>
                          Approve
                        </button>
                        <button type="button" class="review__btn" disabled={busy === p.id} onClick={() => void decline(p)}>
                          Decline
                        </button>
                      </div>
                    </>
                  )}
                  {cardError[p.id] ? <div class="review__card-note">{cardError[p.id]}</div> : null}
                </div>
              );
            })}
          </div>
          {receipts.length > 0 ? (
            <div class="review__receipts">
              {receipts.map((r) => (
                <div class="review__receipt" key={r.id}>
                  {r.line}
                  {r.undo ? (
                    r.undo.guests > 0 ? (
                      <>
                        {' '}
                        <button type="button" class="review__btn" disabled={busy === r.id} onClick={() => void undoReceipt(r, true)}>
                          Undo and tell them
                        </button>
                        <button type="button" class="review__btn" disabled={busy === r.id} onClick={() => void undoReceipt(r, false)}>
                          Undo quietly
                        </button>
                      </>
                    ) : (
                      <>
                        {' '}
                        <button type="button" class="review__btn" disabled={busy === r.id} onClick={() => void undoReceipt(r, undefined)}>
                          Undo
                        </button>
                      </>
                    )
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/** The tab's number — exported for the shell if a badge ever lands on the
 * nav; polled on focus, never on a timer. */
export { countPendingProposals };
